import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSession } from "@/lib/session";
import { Button } from "@/ui/Button";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { cx } from "@/lib/format";
import { NewOrganisation } from "@/ui/frame/Orgs";
import { Logo } from "@/components/Brand";

export const Route = createFileRoute("/_app/")({
  component: HomeScreen,
});

/**
 * The landing page after sign-in.
 *
 * With one organisation there is nothing to choose, so it goes straight there.
 * With none, the only useful thing to offer is creating one.
 */
function HomeScreen() {
  const { organisations } = useSession();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const only = organisations.length === 1 ? organisations[0] : undefined;

  useEffect(() => {
    if (only) void navigate({ to: "/o/$orgId", params: { orgId: only.id }, replace: true });
  }, [only, navigate]);

  if (organisations.length === 0) {
    return (
      <>
        <NewOrganisation open={creating} onOpenChange={setCreating} />

        {/* The first screen of the product, for somebody who has just been
            let in.
            # WHAT IT WAS
            An empty state in a box: a mark, "Create your first organisation",
            one sentence, a button. Correct, and it told a new customer
            nothing about what they had joined or what was about to happen —
            the same component the product uses to say a table has no rows in
            it, standing in for the welcome.
            # WHAT IT IS
            The three steps between here and something running, named, with
            the first one live and the other two greyed. Nobody needs a wizard
            for this — each step happens on its own screen and takes a
            minute — but they do need to know there are three of them and
            that none of them is filling in a form about billing. */}
        <div className="mx-auto flex w-full max-w-xl flex-col gap-8 py-6 sm:py-12">
          <div>
            <Logo height={28} />
            <h1
              className="t-head mt-6"
              style={{ fontSize: "clamp(1.625rem, 3vw, 2.125rem)", lineHeight: 1.15 }}
            >
              You are in.
            </h1>
            <p className="t-body mt-2 max-w-[52ch]">
              Three steps between here and a running service. Each one is its
              own screen and none of them takes longer than a minute.
            </p>
          </div>

          <ol className="flex flex-col">
            <Step
              n="01"
              title="Make an organisation"
              body="Everything belongs to one: your projects, your people, your invoices. Most accounts only ever need the one."
              now
            />
            <Step
              n="02"
              title="Add a project"
              body="A project groups the services, databases and domains of a single application."
            />
            <Step
              n="03"
              title="Deploy a service"
              body="From a repository or an image. It is built, released and watched, and you are told if it goes wrong."
            />
          </ol>

          <div className="flex flex-wrap items-center gap-3">
            <Button tone="primary" onClick={() => setCreating(true)}>
              Make an organisation
            </Button>
            <span className="t-meta">Nothing is charged until something runs.</span>
          </div>
        </div>
      </>
    );
  }

  if (only) return null;

  return (
    <>
      <NewOrganisation open={creating} onOpenChange={setCreating} />
      <div className="flex flex-col gap-6">
        <Head
          title="Your organisations"
          description="You belong to more than one. Pick up where you were."
          actions={
            <Button tone="secondary" onClick={() => setCreating(true)}>
              New organisation
            </Button>
          }
        />

        {/* Rows, like every other list in the product. Two cards across was
            the one place an organisation was drawn as a tile. */}
        <Panel padded={false}>
          <ul>
            {organisations.map((organisation) => (
              <li
                key={organisation.id}
                className="border-b border-[var(--border)] last:border-b-0"
              >
                <button
                  onClick={() =>
                    void navigate({ to: "/o/$orgId", params: { orgId: organisation.id } })
                  }
                  className="row-live flex w-full items-center gap-3 px-5 py-3.5 text-left"
                >
                  <span className="t-strong min-w-0 flex-1 truncate">
                    {organisation.display_name}
                  </span>
                  <span className="mono shrink-0 text-[var(--text-muted)]">
                    {organisation.slug}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </>
  );
}

/**
 * One step on the way in.
 *
 * The step you are on is in full colour with its number in ink; the ones
 * after it are quiet. No ticks, no progress bar, no "1 of 3" — there is
 * nothing to tick yet and a bar that is one third full on arrival is a
 * decoration pretending to be information.
 */
function Step({
  n,
  title,
  body,
  now = false,
}: {
  n: string;
  title: string;
  body: string;
  now?: boolean;
}) {
  return (
    <li
      className={cx(
        "flex gap-5 border-t border-[var(--border)] py-4 first:border-t-0 first:pt-0",
        !now && "opacity-55",
      )}
    >
      <span
        className={cx(
          "section-label shrink-0 pt-[3px] tabular-nums",
          now && "text-[var(--text-primary)]",
        )}
      >
        {n}
      </span>
      <span className="min-w-0">
        <span className="t-strong block">{title}</span>
        <span className="t-body mt-0.5 block">{body}</span>
      </span>
    </li>
  );
}
